import React, { memo, useEffect, useState } from 'react';

export interface HistoryItem {
  _id: string;
  query: string;
  totalResults: number;
  noSiteResults: number;
  createdAt: string;
}

interface SearchHistoryProps {
  onSelect: (query: string) => void;
  onViewAll: () => void;
  refreshTrigger: number;
}

export const SearchHistory: React.FC<SearchHistoryProps> = memo(({ onSelect, onViewAll, refreshTrigger }) => {
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await fetch('/api/search/history');
        const data = await res.json();
        if (data.success) {
          setHistory(data.history || []);
        }
      } catch (err) {
        console.error('Failed to load search history:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [refreshTrigger]);

  if (loading) return null;
  if (history.length === 0) return null;

  const recent = history.slice(0, 5);

  return (
    <div className="max-w-2xl mx-auto mb-8">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Recent Searches
        </h3>
        <button
          onClick={onViewAll}
          className="text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors flex items-center gap-1"
        >
          View All History ({history.length})
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {recent.map((item) => (
          <button
            key={item._id}
            onClick={() => onSelect(item.query)}
            className="group inline-flex items-center gap-2 px-3.5 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 shadow-sm hover:shadow"
            title={`${item.totalResults} results, ${item.noSiteResults} without website • Free (cached)`}
          >
            <svg className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span className="capitalize">{item.query}</span>
            <span className="text-xs text-gray-400 group-hover:text-blue-400">
              {item.totalResults}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
});

SearchHistory.displayName = 'SearchHistory';
export default SearchHistory;
